import React from 'react';
import {Img, staticFile, spring, interpolate} from 'remotion';
import type {Plate} from './timeline';
import {Swing} from './EpisodeComp';

/**
 * गोत्र EP01 — the accepted विष्णु स्मृति plate-split beat (2026-08-22, sanatan-decode-motion §8d) as a CUSTOM plate.
 * Marks (cutlist.marks.VS): pothi · lines · panelL · seal1 · panelM · seal2 · panelR · seal3 (word frames) · relight [3] (P40's three checks).
 * Layers: public_<slug>/custom/vsflow/{vs_bg_plate,static,pothi,lines,panelL,panelM,panelR,seal1,seal2,seal3}.png (full-canvas RGBA).
 */
export type CustomProps = {p: Plate; f: number; fps: number; marks: Record<string, number | number[]>};
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const vsrc = (n: string) => staticFile(`custom/vsflow/${n}.png`);
const VLayer: React.FC<{name: string; style?: React.CSSProperties}> = ({name, style}) => (
  <Img src={vsrc(name)} style={{position: 'absolute', inset: 0, width: '100%', height: '100%', ...style}} />
);

const VsFlow: React.FC<CustomProps> = ({p, f, fps, marks}) => {
  const m = (k: string) => marks[k] as number;
  const since = (k: string) => f - m(k);
  const inn = (k: string, damping = 16, mass = 0.7) => spring({frame: Math.max(0, since(k)), fps, config: {damping, mass, stiffness: 190}});
  const live = (k: string) => since(k) >= 0;
  const pth = inn('pothi', 18, 0.9);
  const lines = clamp01(since('lines') / (fps * 1.35));
  const relight = (k: number) => {
    const at = (marks.relight as number[])[k];
    return f >= at ? interpolate(f - at, [0, 6, 22], [0, 1, 0], {extrapolateRight: 'clamp'}) : 0;
  };
  const panel = (k: string, ox: string) => {
    if (!live(k)) return null;
    const s = inn(k, 15, 0.8);
    return <VLayer name={k} style={{transform: `translateY(${(1 - s) * 300}px) scale(${0.965 + 0.035 * s})`, transformOrigin: `${ox} 82%`}} />;
  };
  const seal = (k: string, ox: string, idx: number) => {
    if (!live(k)) return null;
    const s = inn(k, 11, 0.55);
    const pulse = 1 + 0.14 * relight(idx);
    return <VLayer name={k} style={{transform: `scale(${(2.35 - 1.35 * s) * pulse}) rotate(${-14 * (1 - s)}deg)`, transformOrigin: `${ox} 52%`}} />;
  };
  return (
    <Swing f={f} inAt={p.inFrame} out={p.outFrame} fps={fps} idle={false}>
      <div data-audit="plate:custom-VS" style={{position: 'absolute', inset: 0, backgroundColor: '#F4DFBC'}}>
        <VLayer name="vs_bg_plate" />
        <VLayer name="static" />
        <VLayer name="pothi" style={{transform: `translateY(${(1 - pth) * -560}px) scale(${1.05 - 0.05 * pth})`, transformOrigin: '50% 20%'}} />
        {lines > 0 ? <VLayer name="lines" style={{clipPath: `inset(0 ${50 - 50 * lines}% 0 ${50 - 50 * lines}%)`}} /> : null}
        {panel('panelL', '16.7%')}
        {seal('seal1', '17.1%', 0)}
        {panel('panelM', '50%')}
        {seal('seal2', '50%', 1)}
        {panel('panelR', '83.5%')}
        {seal('seal3', '82.6%', 2)}
      </div>
    </Swing>
  );
};

export const CUSTOM: Record<string, React.FC<CustomProps>> = {VS: VsFlow};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
